import { Mail, Phone, MoreVertical, Clock } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { User } from '../types/user.types';
import { USER_ROLE_CONFIG, USER_STATUS_CONFIG } from '../types/user.types';

interface UserCardProps {
  user: User;
  onEdit?: (user: User) => void;
  onDelete?: (user: User) => void;
}

export function UserCard({ user, onEdit, onDelete }: UserCardProps) {
  const defaultConfig = { label: user.role, color: 'text-gray-700', bgColor: 'bg-gray-100' };
  const roleConfig = USER_ROLE_CONFIG[user.role] ?? defaultConfig;
  const statusConfig = USER_STATUS_CONFIG[user.status] ?? defaultConfig;

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return 'Jamais';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div className="h-10 w-10 rounded-full bg-copper/20 flex items-center justify-center text-copper font-semibold text-sm shrink-0">
              {user.initials}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-base truncate">{user.fullName}</h3>
                <Badge className={`text-xs ${roleConfig.bgColor} ${roleConfig.color}`}>
                  {roleConfig.label}
                </Badge>
                {user.status !== 'active' && (
                  <Badge className={`text-xs ${statusConfig.bgColor} ${statusConfig.color}`}>
                    {statusConfig.label}
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-0.5 truncate">
                {user.email}
              </p>
              <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                <Clock className="h-3 w-3" />
                Dernière connexion : {formatDate(user.lastLoginAt)}
              </p>
            </div>
          </div>

          {(onEdit || onDelete) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {onEdit && (
                  <DropdownMenuItem onClick={() => onEdit(user)}>
                    Modifier
                  </DropdownMenuItem>
                )}
                {onDelete && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-destructive"
                      onClick={() => onDelete(user)}
                    >
                      Supprimer
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Contact info */}
        {(user.phone) && (
          <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t text-sm">
            {user.phone && (
              <a
                href={`tel:${user.phone}`}
                className="flex items-center gap-1 text-muted-foreground hover:text-copper"
              >
                <Phone className="h-3 w-3" />
                {user.phone}
              </a>
            )}
            <a
              href={`mailto:${user.email}`}
              className="flex items-center gap-1 text-muted-foreground hover:text-copper"
            >
              <Mail className="h-3 w-3" />
              Envoyer un email
            </a>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
